/**
 * /romaneios — Lista de Romaneios (Terceiros). Modulo legado, abaixo de
 * Entregas. Le os romaneios persistidos no backend (/api/romaneios).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiRefreshCw, FiSearch } from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import {
  CardItem, CardList, CardMeta, CardSub, CardTitulo, CardTopo,
} from "../../components/ListaCompacta/styles";
import { useAuth } from "../../context/AuthContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  listarRomaneios, SITUACAO_ROMANEIO_LABELS, SITUACAO_ROMANEIO_TONES,
} from "../../services/romaneios";
import { mq as media } from "../../styles/canonical/breakpoints";
import { PageContainer, TableWrapper } from "../../styles/shared";
import {
  EmptyMsg, ErrorMsg, FilterBar, Pill, Select, Table, Td, Th, Tr,
} from "./styles";

// Na meta do card o ano sai com 2 dígitos ("10/11/26"), como no PainelTabela.
function dataCard(iso) {
  return dataCurta(iso).replace(/\/(\d{2})(\d{2})$/, "/$2");
}

function CardRomaneio({ romaneio: r, onAbrir }) {
  return (
    <CardItem
      $clicavel
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onAbrir();
        }
      }}
    >
      <CardTopo>
        <CardTitulo title={r.cliente_nome || r.numero}>{r.cliente_nome || r.numero}</CardTitulo>
        <span className="selo">
          <Pill $tone={SITUACAO_ROMANEIO_TONES[r.situacao]}>
            {SITUACAO_ROMANEIO_LABELS[r.situacao] || r.situacao}
          </Pill>
        </span>
      </CardTopo>
      <CardSub title={r.marcenaria}>
        {[r.marcenaria, r.num_pedido && `pedido ${r.num_pedido}`].filter(Boolean).join(" · ") || "—"}
      </CardSub>
      <CardMeta>
        {r.cliente_nome && (
          <span className="par"><span className="k">nº</span> <b>{r.numero}</b></span>
        )}
        <span className="par">
          {r.cliente_nome && <span className="sep" aria-hidden="true">· </span>}
          <span className="k">criado</span> <b>{dataCard(r.created_at)}</b>
        </span>
        <span className="par">
          <span className="sep" aria-hidden="true">· </span>
          <span className="k">amb.</span> <b>{r.ambientes_count}</b>
        </span>
        <span className="par">
          <span className="sep" aria-hidden="true">· </span>
          <span className="k">itens</span> <b>{r.itens_count}</b>
        </span>
      </CardMeta>
    </CardItem>
  );
}

function dataCurta(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
}

export default function RomaneiosLista() {
  const { request, hasPermission } = useAuth();
  const podeEditar = hasPermission("romaneios:edit");
  const navigate = useNavigate();
  const compacto = useMediaQuery(media.tablet);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busca, setBusca] = useState("");
  const [marcFilter, setMarcFilter] = useState("Todas");
  const [statusFilter, setStatusFilter] = useState("Todos");

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listarRomaneios(request);
      setItems(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar romaneios.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const marcenarias = useMemo(() => {
    const set = new Set(items.map((r) => r.marcenaria).filter(Boolean));
    return ["Todas", ...Array.from(set).sort()];
  }, [items]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return items.filter((r) => {
      const mq =
        !q ||
        [r.numero, r.num_pedido, r.cliente_nome, r.marcenaria].some((v) =>
          String(v || "").toLowerCase().includes(q),
        );
      const mm = marcFilter === "Todas" || r.marcenaria === marcFilter;
      const ms = statusFilter === "Todos" || r.situacao === statusFilter;
      return mq && mm && ms;
    });
  }, [items, busca, marcFilter, statusFilter]);

  const kpis = useMemo(() => {
    const emitidos = items.filter((r) => r.situacao === "EMITIDO").length;
    const rascunhos = items.filter((r) => r.situacao === "RASCUNHO").length;
    const marc = new Set(items.map((r) => r.marcenaria).filter(Boolean)).size;
    return [
      { label: "Total de romaneios", value: items.length, tone: "neutral" },
      { label: "Emitidos", value: emitidos, tone: emitidos > 0 ? "success" : "neutral" },
      { label: "Rascunhos", value: rascunhos, tone: rascunhos > 0 ? "warning" : "neutral" },
      { label: "Marcenarias", value: marc, tone: "neutral" },
    ];
  }, [items]);

  return (
    <PageContainer>
      <EntityHeader
        title="Romaneios — Terceiros"
        subtitle="Carregamento de obras em marcenarias terceiras"
        primaryAction={
          podeEditar
            ? {
                label: "Novo romaneio",
                icon: <FiPlus size={14} />,
                onClick: () => navigate("/romaneios/novo"),
              }
            : undefined
        }
        actions={[{ label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar }]}
      />

      <KpiStrip items={kpis} />

      <FilterBar>
        <div className="search">
          <FiSearch size={15} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por pedido, cliente ou marcenaria…"
          />
        </div>
        <Select value={marcFilter} onChange={(e) => setMarcFilter(e.target.value)}>
          {marcenarias.map((m) => (
            <option key={m} value={m}>
              {m === "Todas" ? "Todas as marcenarias" : m}
            </option>
          ))}
        </Select>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {["Todos", "RASCUNHO", "EMITIDO"].map((s) => (
            <option key={s} value={s}>
              {s === "Todos" ? "Todos os status" : SITUACAO_ROMANEIO_LABELS[s]}
            </option>
          ))}
        </Select>
      </FilterBar>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {loading ? (
        <EmptyMsg>Carregando…</EmptyMsg>
      ) : filtrados.length === 0 ? (
        <EmptyMsg>
          {podeEditar
            ? "Nenhum romaneio encontrado. Clique em “Novo romaneio”."
            : "Nenhum romaneio encontrado."}
        </EmptyMsg>
      ) : compacto ? (
        <CardList data-testid="romaneios-cards">
          {filtrados.map((r) => (
            <CardRomaneio key={r.id} romaneio={r} onAbrir={() => navigate(`/romaneios/${r.id}`)} />
          ))}
        </CardList>
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <Th>Nº</Th>
                <Th>Pedido</Th>
                <Th>Cliente</Th>
                <Th>Marcenaria</Th>
                <Th style={{ textAlign: "center" }}>Amb.</Th>
                <Th style={{ textAlign: "center" }}>Itens</Th>
                <Th>Status</Th>
                <Th>Criado</Th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((r) => (
                <Tr key={r.id} onClick={() => navigate(`/romaneios/${r.id}`)}>
                  <Td className="mono">{r.numero}</Td>
                  <Td>{r.num_pedido}</Td>
                  <Td className="ellipsis" title={r.cliente_nome}>{r.cliente_nome || "—"}</Td>
                  <Td className="ellipsis" title={r.marcenaria}>{r.marcenaria}</Td>
                  <Td style={{ textAlign: "center" }}>{r.ambientes_count}</Td>
                  <Td style={{ textAlign: "center" }}>{r.itens_count}</Td>
                  <Td>
                    <Pill $tone={SITUACAO_ROMANEIO_TONES[r.situacao]}>
                      {SITUACAO_ROMANEIO_LABELS[r.situacao] || r.situacao}
                    </Pill>
                  </Td>
                  <Td className="mono">{dataCurta(r.created_at)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </PageContainer>
  );
}
